import { useSyncExternalStore } from "react";

/**
 * Tracks whether the viewer is currently "in League mode" (on a live league they
 * belong to). The global site header subscribes to this so it can swap itself
 * for a branded league header — league coins instead of the personal wallet.
 *
 * Kept intentionally tiny and separate from the league data store: it holds only
 * the active league id, and consumers read the league itself from the league store.
 *
 * Do not import `leagueGamePlayUrl` here — that module pulls Sixes/Squares/Bullion
 * fixtures, which import this store and make Parcel fail with
 * `Cannot find module '.../leagueModeStore'`.
 */

/** Same key as `LEAGUE_ID_QUERY` in leagueGamePlayUrl (duplicated to avoid that cycle). */
const LEAGUE_ID_QUERY = "leagueId";

function leagueIdFromWindow(): string | null {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get(LEAGUE_ID_QUERY);
    return value?.trim() ? value.trim() : null;
}

/** Seed from the URL so a soft/hard land on `?leagueId=` never paints the site header first. */
let activeLeagueId: string | null = leagueIdFromWindow();
const listeners = new Set<() => void>();

function emit(): void {
    listeners.forEach((listener) => listener());
}

export function setActiveLeagueMode(leagueId: string | null): void {
    if (activeLeagueId === leagueId) return;
    activeLeagueId = leagueId;
    emit();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): string | null {
    return activeLeagueId;
}

/** Read the active league id without subscribing (URL builders, click handlers). */
export function peekActiveLeagueId(): string | null {
    return activeLeagueId;
}

/** The id of the league whose mode is currently active, or null. */
export function useActiveLeagueMode(): string | null {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
